// 💰 Anticipos — Gestión CxC / CxP
// Registra anticipos (pagos recibidos/entregados antes de la factura, ligados a
// un partner) y los aplica — parcial y a varias facturas — rebajando el saldo
// de cada factura. Cada aplicación es un abono forma_pago='anticipo'; el
// disponible del anticipo = monto − aplicado. Todo reversible.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Filter, Loader2, Plus, RotateCcw, Trash2, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const FORMAS_PAGO = ['transferencia', 'efectivo', 'cheque', 'tarjeta', 'otro'];
const usd = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

interface ModoAnt {
  titulo: string;
  descripcion: string;
  contraparte: string;          // Cliente / Proveedor
  rpcCrear: string;
  rpcListar: string;
  rpcEliminar: string;
  rpcFacturas: string;
  rpcAplicar: string;
  rpcDesaplicar: string;
}

const MODO_CXC: ModoAnt = {
  titulo: 'Anticipos de Clientes',
  descripcion: 'Anticipos de clientes y su aplicación a facturas por cobrar',
  contraparte: 'Cliente',
  rpcCrear: 'cxc_anticipo_crear', rpcListar: 'cxc_anticipo_listar', rpcEliminar: 'cxc_anticipo_eliminar',
  rpcFacturas: 'cxc_anticipo_facturas', rpcAplicar: 'cxc_anticipo_aplicar', rpcDesaplicar: 'cxc_anticipo_desaplicar',
};
const MODO_CXP: ModoAnt = {
  titulo: 'Anticipos a Proveedores',
  descripcion: 'Anticipos a proveedores y su aplicación a facturas por pagar',
  contraparte: 'Proveedor',
  rpcCrear: 'cxp_anticipo_crear', rpcListar: 'cxp_anticipo_listar', rpcEliminar: 'cxp_anticipo_eliminar',
  rpcFacturas: 'cxp_anticipo_facturas', rpcAplicar: 'cxp_anticipo_aplicar', rpcDesaplicar: 'cxp_anticipo_desaplicar',
};

export function CxcAnticipos() { return <Anticipos cfg={MODO_CXC} icono={Coins} />; }
export function CxpAnticipos() { return <Anticipos cfg={MODO_CXP} icono={Coins} />; }

interface Anticipo {
  id: string; codigo: string; fecha: string; monto: number;
  forma_pago: string | null; referencia: string | null;
  partner_id: string; partner: string; identificacion: string | null;
  aplicado: number; disponible: number;
}
interface Partner { id: string; razon_social: string; identificacion: string; }
interface FacturaAplicable {
  ticket_id: string; serie_comprobante: string; fecha_emision: string | null;
  importe_total: number; saldo: number; aplicado_este: number;
}

const selectCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';
const inputCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

function Anticipos({ cfg, icono: Icono }: { cfg: ModoAnt; icono: LucideIcon }) {
  const queryClient = useQueryClient();
  const [estado, setEstado] = useState('disponibles');
  const [busqueda, setBusqueda] = useState('');
  const [aplicada, setAplicada] = useState({ estado: 'disponibles', partner: '' });
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [anticipoAplicar, setAnticipoAplicar] = useState<Anticipo | null>(null);

  const { data: anticipos = [], isFetching } = useQuery<Anticipo[]>({
    queryKey: ['anticipos', cfg.rpcListar, aplicada],
    queryFn: async () =>
      (await apiClient.post(`/rpc/${cfg.rpcListar}`, { _estado: aplicada.estado, _partner: aplicada.partner || null }, FIN)).data || [],
  });

  const { data: partners = [] } = useQuery<Partner[]>({
    queryKey: ['sriPartners'],
    queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
    staleTime: 60_000,
  });

  const refrescar = () => {
    void queryClient.invalidateQueries({ queryKey: ['anticipos'] });
    void queryClient.invalidateQueries({ queryKey: ['cartera'] });
  };

  const eliminar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post(`/rpc/${cfg.rpcEliminar}`, { _anticipo_id: id }, FIN)).data,
    onSuccess: () => { toast.success('Anticipo eliminado'); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo eliminar'),
  });

  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-6">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Icono className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{cfg.titulo}</h1>
            <p className="text-sm text-muted-foreground">{cfg.descripcion}</p>
          </div>
        </div>
        <Button onClick={() => setNuevoOpen(true)}><Plus className="w-4 h-4" /> Nuevo anticipo</Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="an-estado">Estado</label>
              <select id="an-estado" className={selectCls} value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="disponibles">Con disponible</option>
                <option value="agotados">Agotados</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="an-partner">{cfg.contraparte}</label>
              <input id="an-partner" className={`${inputCls} w-56`} placeholder="Razón social o RUC…"
                value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setAplicada({ estado, partner: busqueda }); }} />
            </div>
            <Button size="sm" onClick={() => setAplicada({ estado, partner: busqueda })} isLoading={isFetching}>
              {!isFetching && <Filter className="w-4 h-4" />} Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de anticipos */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Anticipos ({anticipos.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Código</th>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">{cfg.contraparte}</th>
                <th className="py-2 pr-3">Forma</th>
                <th className="py-2 pr-3 text-right">Monto</th>
                <th className="py-2 pr-3 text-right">Aplicado</th>
                <th className="py-2 pr-3 text-right">Disponible</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {anticipos.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin anticipos</td></tr>
              )}
              {anticipos.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{a.codigo}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{a.fecha}</td>
                  <td className="py-2 pr-3">
                    <p className="truncate max-w-56" title={a.partner}>{a.partner}</p>
                    <p className="text-xs text-muted-foreground">{a.identificacion || ''}</p>
                  </td>
                  <td className="py-2 pr-3">{a.forma_pago || '—'}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(a.monto)}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-green-600 dark:text-green-400">
                    {a.aplicado > 0 ? usd(a.aplicado) : '—'}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(a.disponible)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant={a.disponible > 0 ? 'default' : 'outline'}
                      onClick={() => setAnticipoAplicar(a)}>Aplicar</Button>
                    {a.aplicado === 0 && (
                      <button className="inline-flex items-center justify-center w-7 h-7 ml-1 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 align-middle"
                        title="Eliminar anticipo"
                        onClick={async () => { if (await dlg.confirmar(`¿Eliminar el anticipo ${a.codigo}?`, { peligro: true })) eliminar.mutate(a.id); }}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {nuevoOpen && (
        <ModalNuevo cfg={cfg} partners={partners} onClose={() => setNuevoOpen(false)}
          onSaved={() => { setNuevoOpen(false); refrescar(); }} />
      )}
      {anticipoAplicar && (
        <ModalAplicar cfg={cfg} anticipo={anticipoAplicar} onClose={() => setAnticipoAplicar(null)} onChanged={refrescar} />
      )}
    </div>
  );
}

// --- Registrar anticipo --------------------------------------------------------
function ModalNuevo({ cfg, partners, onClose, onSaved }: {
  cfg: ModoAnt; partners: Partner[]; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState({
    partner_id: '', monto: '', fecha: fechaLocal(),
    forma_pago: 'transferencia', referencia: '', observaciones: '',
  });
  const crear = useMutation({
    mutationFn: async () => (await apiClient.post(`/rpc/${cfg.rpcCrear}`, {
      _partner_id: form.partner_id, _monto: Number(form.monto), _fecha: form.fecha,
      _forma_pago: form.forma_pago || null, _referencia: form.referencia || null,
      _observaciones: form.observaciones || null,
    }, FIN)).data,
    onSuccess: (r: any) => { toast.success(`${r.message} (${r.codigo})`); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo registrar'),
  });
  return (
    <PanelIntegrado title={`Nuevo anticipo de ${cfg.contraparte.toLowerCase()}`} onClose={onClose}>
      <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="na-partner">{cfg.contraparte} *</label>
            <select id="na-partner" className={`${selectCls} w-full`} value={form.partner_id}
              onChange={(e) => setForm({ ...form, partner_id: e.target.value })}>
              <option value="">— Seleccione —</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion})</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="na-fecha">Fecha</label>
              <input id="na-fecha" type="date" className={inputCls} value={form.fecha}
                max={fechaLocal()}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="na-monto">Monto *</label>
              <input id="na-monto" type="number" min="0.01" step="0.01" className={`${inputCls} w-32 text-right`}
                placeholder="0.00" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} />
            </div>
            <div className="space-y-1 flex-1">
              <label className="text-xs font-medium" htmlFor="na-forma">Forma de pago</label>
              <select id="na-forma" className={`${selectCls} w-full`} value={form.forma_pago}
                onChange={(e) => setForm({ ...form, forma_pago: e.target.value })}>
                {FORMAS_PAGO.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="na-ref">Referencia</label>
            <input id="na-ref" className={`${inputCls} w-full`} placeholder="Nº transferencia / comprobante…"
              value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="na-obs">Observaciones</label>
            <input id="na-obs" className={`${inputCls} w-full`} placeholder="Opcional"
              value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button isLoading={crear.isPending} onClick={() => {
              if (!form.partner_id) { toast.error(`Seleccione el ${cfg.contraparte.toLowerCase()}`); return; }
              if (!Number(form.monto)) { toast.error('Ingrese el monto'); return; }
              crear.mutate();
            }}>{!crear.isPending && <Plus className="w-4 h-4" />} Registrar</Button>
          </div>
      </div>
    </PanelIntegrado>
  );
}

// --- Aplicar anticipo a facturas ----------------------------------------------
function ModalAplicar({ cfg, anticipo, onClose, onChanged }: {
  cfg: ModoAnt; anticipo: Anticipo; onClose: () => void; onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: facturas = [], isLoading } = useQuery<FacturaAplicable[]>({
    queryKey: ['anticipoFacturas', cfg.rpcFacturas, anticipo.id],
    queryFn: async () => (await apiClient.post(`/rpc/${cfg.rpcFacturas}`, { _anticipo_id: anticipo.id }, FIN)).data || [],
  });
  const [montos, setMontos] = useState<Record<string, string>>({});

  // disponible local recalculado al refrescar
  const { data: ant } = useQuery<Anticipo | undefined>({
    queryKey: ['anticipoActual', cfg.rpcListar, anticipo.id],
    queryFn: async () => {
      const all: Anticipo[] = (await apiClient.post(`/rpc/${cfg.rpcListar}`, { _estado: 'todos' }, FIN)).data || [];
      return all.find((a) => a.id === anticipo.id);
    },
    initialData: anticipo,
  });
  const disponible = ant?.disponible ?? anticipo.disponible;

  const refrescarTodo = () => {
    void queryClient.invalidateQueries({ queryKey: ['anticipoFacturas'] });
    void queryClient.invalidateQueries({ queryKey: ['anticipoActual'] });
    onChanged();
  };

  const aplicar = useMutation({
    mutationFn: async ({ ticketId, monto }: { ticketId: string; monto: number }) =>
      (await apiClient.post(`/rpc/${cfg.rpcAplicar}`, { _anticipo_id: anticipo.id, _ticket_id: ticketId, _monto: monto }, FIN)).data,
    onSuccess: (r: any) => { toast.success(`${r.message} (${usd(r.monto)}) · disponible ${usd(r.disponible)}`); refrescarTodo(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo aplicar'),
  });
  const desaplicar = useMutation({
    mutationFn: async (ticketId: string) =>
      (await apiClient.post(`/rpc/${cfg.rpcDesaplicar}`, { _anticipo_id: anticipo.id, _ticket_id: ticketId }, FIN)).data,
    onSuccess: () => { toast.success('Aplicación revertida'); refrescarTodo(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo revertir'),
  });

  return (
    <PanelIntegrado title={`Aplicar anticipo ${anticipo.codigo}`} onClose={onClose} wide>
      <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {anticipo.partner} · monto {usd(anticipo.monto)} ·
            <span className="font-semibold text-foreground"> disponible {usd(disponible)}</span>
          </p>
          {isLoading ? (
            <div className="py-10 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-primary" /></div>
          ) : facturas.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              El {cfg.contraparte.toLowerCase()} no tiene facturas con saldo en cartera.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Factura</th>
                  <th className="py-2 pr-3">Emisión</th>
                  <th className="py-2 pr-3 text-right">Saldo</th>
                  <th className="py-2 pr-3 text-right">De este anticipo</th>
                  <th className="py-2 pr-3 text-right">Aplicar</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {facturas.map((f) => {
                  const max = Math.min(f.saldo, disponible);
                  return (
                    <tr key={f.ticket_id} className="border-b last:border-0">
                      <td className="py-2 pr-3 whitespace-nowrap">{f.serie_comprobante}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{f.fecha_emision}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(f.saldo)}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap text-green-600 dark:text-green-400">
                        {f.aplicado_este > 0 ? usd(f.aplicado_este) : '—'}</td>
                      <td className="py-2 pr-3 text-right">
                        <input type="number" min="0.01" step="0.01" max={max}
                          className={`${inputCls} w-28 text-right`} placeholder={usd(max)}
                          value={montos[f.ticket_id] || ''}
                          disabled={max <= 0}
                          onChange={(e) => setMontos({ ...montos, [f.ticket_id]: e.target.value })} />
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <Button size="sm" disabled={max <= 0} isLoading={aplicar.isPending}
                          onClick={() => {
                            const m = Number(montos[f.ticket_id] || max);
                            if (!m || m <= 0) { toast.error('Ingrese un monto'); return; }
                            if (m > max + 0.005) { toast.error(`Máximo aplicable: ${usd(max)}`); return; }
                            aplicar.mutate({ ticketId: f.ticket_id, monto: m });
                            setMontos({ ...montos, [f.ticket_id]: '' });
                          }}>Aplicar</Button>
                        {f.aplicado_este > 0 && (
                          <button className="inline-flex items-center justify-center w-7 h-7 ml-1 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 align-middle"
                            title="Revertir aplicación en esta factura"
                            onClick={() => desaplicar.mutate(f.ticket_id)}>
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            Deja el monto vacío para aplicar el máximo posible (mínimo entre el saldo de la factura y el disponible del anticipo).
          </p>
      </div>
    </PanelIntegrado>
  );
}
